import { Button } from '@chakra-ui/button';
import { Checkbox } from '@chakra-ui/checkbox';
import Icon from '@chakra-ui/icon';
import { Input } from '@chakra-ui/input';
import { Divider, Flex, Heading, SimpleGrid, Text } from '@chakra-ui/layout';
import { useBreakpointValue } from '@chakra-ui/media-query';
import { MenuButton, MenuItemOption, MenuOptionGroup } from '@chakra-ui/menu';
import { Spinner } from '@chakra-ui/spinner';
import { Tag } from '@chakra-ui/tag';
import { Textarea } from '@chakra-ui/textarea';
import { useToast } from '@chakra-ui/toast';
import firebase from 'firebase';
import moment from 'moment';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Filter } from 'react-feather';
import { Card } from '../../../components/Card';
import { Pagination } from '../../../components/Pagination';
import { Menu } from '../../../components/styled-chakra/Menu';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import {
  FirebaseUsersResponse,
  FirebaseUserWithData,
} from '../../../types/types';
import { isInDevelopment } from '../../../utils/detectEnv';
import { expired } from '../../../utils/expired';

const getStatusContent = (user: FirebaseUserWithData) => {
  if (user.status === 'active') return { colorScheme: 'green', label: 'Aktiv' };
  if (user.status === 'inactive')
    return { colorScheme: 'red', label: 'Inaktiv' };
  if (user.status === 'trial') return { colorScheme: 'yellow', label: 'Test' };
  if (user.status === 'trial_ended')
    return { colorScheme: 'orange', label: 'Test abgelaufen' };
  return { colorScheme: '', label: '' };
};

interface AdminUsersTabProps {}

export const AdminUsersTab: React.FC<AdminUsersTabProps> = () => {
  const currentUser = useCurrentUser();
  const [users, setUsers] = useState<FirebaseUsersResponse | null>(null);
  const [statusFilter, setStatusFilter] = useState<
    FirebaseUsersResponse[0]['status'] | ''
  >('');
  const [fetchUsersTrigger, setFetchUsersTrigger] = useState(false);
  const [contactedFilter, setContactedFilter] = useState<
    FirebaseUsersResponse[0]['adminContactStatus'] | ''
  >('');
  const [searchTermFilter, setSearchTermFilter] = useState<string>('');
  const [adminNotes, setAdminNotes] = useState<{ uid: string; value: string }>({
    uid: '',
    value: '',
  });
  const [pageNumber, setPageNumber] = useState(1);
  const [sorting, setSorting] = useState<{
    type: 'alphabetical' | 'lastCreated';
    order: 'asc' | 'desc';
  }>({ type: 'lastCreated', order: 'asc' });

  const isTabletOrBelow = useBreakpointValue([true, true, false]);
  const toast = useToast();

  useEffect(() => {
    firebase
      .app()
      .functions(isInDevelopment ? undefined : 'europe-west3')
      .httpsCallable('users')()
      .then((data) => setUsers(data.data))
      .catch((e) => {
        console.error(e);
        toast({ title: 'Fehler bei der Nutzerabfrage', status: 'error' });
      });
  }, [fetchUsersTrigger]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        if (adminNotes.uid === '') return;
        await firebase
          .firestore()
          .collection('users')
          .doc(adminNotes.uid)
          .update({
            adminNotes: adminNotes.value,
          });
        setFetchUsersTrigger(!fetchUsersTrigger);
        toast({
          title: 'Erfolgreich gespeichert',
          duration: 2500,
          status: 'success',
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
        toast({
          title: 'Beim Speichern ist ein Fehler aufgetreten',
          duration: 5000,
          status: 'error',
        });
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [adminNotes]);

  const filteredUsers = useMemo(() => {
    const res =
      users &&
      users
        .map((user) => ({
          ...user,
          status:
            expired(user) && user.status === 'trial'
              ? 'trial_ended'
              : user.status,
        }))
        .filter(
          (user) =>
            !user.isAdmin &&
            (statusFilter ? user.status === statusFilter : true)
        )
        .filter(
          (user) =>
            (searchTermFilter
              ? user.email
                  .toLowerCase()
                  .includes(searchTermFilter.toLowerCase()) ||
                user.displayName
                  .toLowerCase()
                  .includes(searchTermFilter.toLowerCase()) ||
                user.adminNotes
                  .toLowerCase()
                  .includes(searchTermFilter.toLowerCase())
              : true) &&
            (contactedFilter
              ? user.adminContactStatus === contactedFilter
              : true)
        )
        .sort((a, b) => {
          if (
            sorting.type === 'lastCreated' &&
            a.metadata.creationTime &&
            b.metadata.creationTime
          ) {
            const aCreated = new Date(a.metadata.creationTime).getTime();
            const bCreated = new Date(b.metadata.creationTime).getTime();
            if (sorting.order === 'asc') {
              return bCreated - aCreated;
            }
            return aCreated - bCreated;
          }
          if (sorting.type === 'alphabetical') {
            const aTitle = a.email.toLowerCase();
            const bTitle = b.email.toLowerCase();
            if (sorting.order === 'asc') {
              return aTitle < bTitle ? -1 : 1;
            }
            return aTitle > bTitle ? -1 : 1;
          }
          return 0;
        });
    if (res && pageNumber > Math.ceil(res.length / 10)) {
      setPageNumber(1);
    }
    return res;
  }, [
    users,
    statusFilter,
    searchTermFilter,
    pageNumber,
    contactedFilter,
    sorting,
  ]);

  if (!currentUser || !users)
    return (
      <Flex w="full" justify="center">
        <Spinner />
      </Flex>
    );

  console.log(
    users.map((user) => ({
      count: user.courseCount,
      minutes: user.courseMinutes,
      name: user.email,
    }))
  );

  console.log(
    users.reduce((prev, curr) => prev + (curr.courseCount > 0 ? 1 : 0), 0)
  );

  const onAdminUserStatusChange = async (
    e: ChangeEvent<HTMLInputElement>,
    uid: string
  ) => {
    try {
      await firebase
        .firestore()
        .collection('users')
        .doc(uid)
        .update({
          adminContactStatus: e.target.checked ? 'contacted' : 'not_contacted',
        });
      setFetchUsersTrigger(!fetchUsersTrigger);
      toast({
        title: 'Erfolgreich gespeichert',
        duration: 2500,
        status: 'success',
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
      toast({
        title: 'Beim Speichern ist ein Fehler aufgetreten',
        duration: 5000,
        status: 'error',
      });
    }
  };

  // const onUserPasswordReset = (email: string) => {
  //   firebase
  //     .app()
  //     .functions(isInDevelopment ? undefined : 'europe-west3')
  //     .httpsCallable('sendUserPasswordReset')({ email });
  // };

  return (
    <div>
      <Flex justify="space-between" w="full" mb={4}>
        <Heading size="md">Mitglieder & Leads</Heading>
        <Pagination
          currentPage={pageNumber}
          items={filteredUsers}
          onPageChange={setPageNumber}
        />
      </Flex>
      <Flex mb={6}>
        <Input
          focusBorderColor="brand.500"
          placeholder="Name, Email oder Notizen durchsuchen"
          borderRadius="xl"
          onChange={(e) => setSearchTermFilter(e.target.value)}
        />
        <Menu
          menuProps={{
            placement: 'bottom-end',
            gutter: 5,
            closeOnSelect: false,
          }}
          triggerComponent={
            <MenuButton
              flexShrink={0}
              borderRadius="xl"
              ml={3}
              leftIcon={
                <Icon
                  as={Filter}
                  color={
                    (statusFilter || contactedFilter) !== '' ? 'green' : 'black'
                  }
                />
              }
              as={Button}
              color={
                (statusFilter || contactedFilter) !== '' ? 'green' : 'black'
              }
            >
              Filtern
            </MenuButton>
          }
        >
          <MenuOptionGroup
            defaultValue="lastCreated"
            title="Sortieren nach"
            onChange={(v: any) => setSorting({ ...sorting, type: v })}
          >
            <MenuItemOption value="lastCreated">
              zuletzt registriert
            </MenuItemOption>
            <MenuItemOption value="alphabetical">alphabetisch</MenuItemOption>
          </MenuOptionGroup>
          <MenuOptionGroup
            defaultValue=""
            title="Status"
            onChange={(v: any) => setStatusFilter(v)}
          >
            <MenuItemOption value="">alle</MenuItemOption>
            <MenuItemOption value="active">aktiv</MenuItemOption>
            <MenuItemOption value="trial">Test</MenuItemOption>
            <MenuItemOption value="trial_ended">Test abgelaufen</MenuItemOption>
            <MenuItemOption value="inactive">inaktiv</MenuItemOption>
          </MenuOptionGroup>
          <MenuOptionGroup
            defaultValue=""
            title="Kontaktiert"
            onChange={(v: any) => setContactedFilter(v)}
          >
            <MenuItemOption value="">alle</MenuItemOption>
            <MenuItemOption value="contacted">
              bereits kontaktiert
            </MenuItemOption>
            <MenuItemOption value="not_contacted">
              nicht kontaktiert
            </MenuItemOption>
          </MenuOptionGroup>
        </Menu>
      </Flex>
      {filteredUsers &&
        filteredUsers
          .slice((pageNumber - 1) * 10, pageNumber * 10)
          .map((user, index) => (
            <Card
              collapsable={true}
              isDefaultCollapsed={true}
              key={user.email}
              title={`${user.email} (${user.displayName})`}
              animate={true}
              containerProps={{ mb: 4 }}
              headerComponent={
                <Flex align="center">
                  <Tag
                    ml={2}
                    whiteSpace="nowrap"
                    variant="subtle"
                    textTransform="uppercase"
                    colorScheme={getStatusContent(user).colorScheme}
                    size="md"
                  >
                    {getStatusContent(user).label}
                  </Tag>
                </Flex>
              }
            >
              <SimpleGrid columns={[1, 1, 2]} gap={3}>
                <Text
                  lineHeight="1.7"
                  textTransform="uppercase"
                  fontWeight="bold"
                  fontSize="sm"
                >
                  Name
                </Text>
                <Text flex={1}>{user.displayName}</Text>
                <Divider
                  gridColumnStart={!isTabletOrBelow ? '1' : ''}
                  gridColumnEnd={!isTabletOrBelow ? '3' : ''}
                />
                <Text
                  lineHeight="1.7"
                  textTransform="uppercase"
                  fontWeight="bold"
                  fontSize="sm"
                >
                  E-Mail
                </Text>
                <Text>{user.email}</Text>
                <Divider
                  gridColumnStart={!isTabletOrBelow ? '1' : ''}
                  gridColumnEnd={!isTabletOrBelow ? '3' : ''}
                />
                <Text
                  lineHeight="1.7"
                  textTransform="uppercase"
                  fontWeight="bold"
                  fontSize="sm"
                >
                  Telefon
                </Text>
                <Text>{user.telephone}</Text>
                <Divider
                  gridColumnStart={!isTabletOrBelow ? '1' : ''}
                  gridColumnEnd={!isTabletOrBelow ? '3' : ''}
                />
                {user.status !== 'active' && (
                  <>
                    <Text
                      lineHeight="1.7"
                      textTransform="uppercase"
                      fontWeight="bold"
                      fontSize="sm"
                    >
                      Ablauf Testversion
                    </Text>
                    <Text>
                      {moment(user.trialExpiration).format('LL')} (
                      {`${
                        user.status === 'trial_ended' ? 'seit' : 'in'
                      } ${Math.abs(
                        moment(user.trialExpiration).diff(moment(), 'day')
                      )} Tagen`}
                      )
                    </Text>
                    <Divider
                      gridColumnStart={!isTabletOrBelow ? '1' : ''}
                      gridColumnEnd={!isTabletOrBelow ? '3' : ''}
                    />
                  </>
                )}
                <Text
                  lineHeight="1.7"
                  textTransform="uppercase"
                  fontWeight="bold"
                  fontSize="sm"
                >
                  Registriert seit
                </Text>
                <Text>{moment(user.metadata.creationTime).format('LL')}</Text>
                <Divider
                  gridColumnStart={!isTabletOrBelow ? '1' : ''}
                  gridColumnEnd={!isTabletOrBelow ? '3' : ''}
                />
                <Text
                  lineHeight="1.7"
                  textTransform="uppercase"
                  fontWeight="bold"
                  fontSize="sm"
                >
                  Zuletzt online
                </Text>
                <Text>
                  {moment(user.metadata.lastSignInTime).format('LLL')}
                </Text>
                <Divider
                  gridColumnStart={!isTabletOrBelow ? '1' : ''}
                  gridColumnEnd={!isTabletOrBelow ? '3' : ''}
                />
                <Text
                  lineHeight="1.7"
                  textTransform="uppercase"
                  fontWeight="bold"
                  fontSize="sm"
                >
                  Gutschein aktiviert
                </Text>
                <Text color={user.isVoucher ? 'green' : 'red'}>
                  {user.isVoucher ? 'ja' : 'nein'}
                </Text>
                <Divider
                  gridColumnStart={!isTabletOrBelow ? '1' : ''}
                  gridColumnEnd={!isTabletOrBelow ? '3' : ''}
                />
                <Text
                  lineHeight="1.7"
                  textTransform="uppercase"
                  fontWeight="bold"
                  fontSize="sm"
                >
                  bereits kontaktiert
                </Text>
                <Checkbox
                  defaultChecked={user.adminContactStatus === 'contacted'}
                  colorScheme="brand"
                  onChange={(e) => onAdminUserStatusChange(e, user.uid)}
                />
                <Divider
                  gridColumnStart={!isTabletOrBelow ? '1' : ''}
                  gridColumnEnd={!isTabletOrBelow ? '3' : ''}
                />
                <Text
                  lineHeight="1.7"
                  textTransform="uppercase"
                  fontWeight="bold"
                  fontSize="sm"
                >
                  Notizen
                </Text>
                <Textarea
                  onChange={(e) =>
                    setAdminNotes({ uid: user.uid, value: e.target.value })
                  }
                  defaultValue={user.adminNotes}
                  colorScheme="brand"
                  focusBorderColor="brand.500"
                />
              </SimpleGrid>
              <Text
                mt={2}
                color="gray.500"
                w="full"
                textAlign="right"
                fontSize="8px"
              >
                ID: {user.uid}
              </Text>
            </Card>
          ))}
      <Pagination
        containerProps={{ mt: 4 }}
        currentPage={pageNumber}
        items={filteredUsers}
        onPageChange={setPageNumber}
      />
    </div>
  );
};
